import { CheckCircle, ChevronLeft, ChevronRight, Clock, LogIn, XCircle } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { RecentLoginsProps } from "./interfaces/interface";
import { Badge } from "../../ui/badge";
import { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Skeleton } from "../interfaces/CommonInterfaces";
import OverviewCardHeader from "./OverviewCardHeader";
import OverviewEmptyState from "./OverviewEmptyState";

export default function RecentLoginDetails({ fromdetails = false, recentLogins = [], loading, description = "Recent sign-in and sign-out events recorded on this endpoint" }: RecentLoginsProps) {
  const [currentLoginPage, setCurrentLoginPage] = useState(1);

  const itemsPerPage = 7;
  const totalLoginPages = Math.ceil(recentLogins.length / itemsPerPage);
  const loginStartIndex = (currentLoginPage - 1) * itemsPerPage;
  const paginatedLogins = recentLogins.slice(loginStartIndex, loginStartIndex + itemsPerPage);

  useEffect(() => {
    setCurrentLoginPage(1);
  }, [recentLogins]);

  useEffect(() => {
    if (totalLoginPages > 0 && currentLoginPage > totalLoginPages) {
      setCurrentLoginPage(totalLoginPages);
    }
  }, [currentLoginPage, totalLoginPages]);

  const getVisiblePages = () => {
    if (totalLoginPages <= 30) {
      return Array.from({ length: totalLoginPages }, (_, i) => i + 1);
    }

    const visible: (number | string)[] = [];

    if (currentLoginPage <= 10) {
      visible.push(...Array.from({ length: 10 }, (_, i) => i + 1));
      visible.push("...");
      visible.push(totalLoginPages);
    } else if (currentLoginPage >= totalLoginPages - 9) {
      visible.push(1);
      visible.push("...");
      visible.push(
        ...Array.from({ length: 10 }, (_, i) => totalLoginPages - 9 + i)
      );
    } else {
      visible.push(1);
      visible.push("...");
      visible.push(currentLoginPage - 1, currentLoginPage, currentLoginPage + 1);
      visible.push("...");
      visible.push(totalLoginPages);
    }

    return visible;
  };


  const extractVal = (str: string) => {
    return str.includes("logged off") ? "logoff" : "login";
  };

  return (
    <Card className="overview-card overview-login-card flex flex-col flex-1">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Recent Logins"
        description={description}
        icon={LogIn}
        detail={!loading && recentLogins.length > 0 ? `${recentLogins.length.toLocaleString()} records` : undefined}
        className="pb-3"
        titleClassName="text-base"
      />
      <CardContent className="overview-card-content flex flex-col flex-1">
        <div className="overview-login-list flex-1 pr-1 space-y-2 overflow-y-auto">
          {loading
            ? // ✅ Skeleton placeholders while loading
            [...Array(7)].map((_, i) => (
              <div key={i} className="overview-login-entry p-2 border rounded-md bg-background/50">
                <div className="flex items-center justify-between mb-1">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="w-16 h-4 rounded-full" />
                </div>
                <Skeleton className="w-full h-3 mb-1" />
                <Skeleton className="w-32 h-3" />
              </div>
            ))
            : paginatedLogins.length > 0 ?
              paginatedLogins.map((login) => (
                <div
                  key={login.id}
                  className="overview-login-entry p-2 border rounded-md bg-background/50 hover-elevate"
                  data-testid={`login-${login.id}`}
                >
                  <div className="overview-login-identity flex items-center justify-between mb-1">
                    <div className="overview-login-computer text-sm font-medium text-foreground">{login.computerName}</div>
                    <Badge
                      variant={extractVal(login.message) === "logoff" ? "destructive" : "default"}
                      className="overview-login-status flex items-center text-xs"
                      data-action={extractVal(login.message)}
                    >
                      {extractVal(login.message) === "logoff" ? (
                        <XCircle className="w-3 h-3 mr-1" />
                      ) : (
                        <CheckCircle className="w-3 h-3 mr-1" />
                      )}
                      {extractVal(login.message)}
                    </Badge>
                  </div>
                  <div className="overview-login-message text-xs break-all text-muted-foreground">
                    {login.message}
                  </div>
                  <div className="overview-login-time text-xs text-muted-foreground">{fromdetails && <Clock aria-hidden="true" />}{login.updatedAt}</div>
                </div>
              )) : fromdetails ? (
                <OverviewEmptyState icon={LogIn} message="No recent logins found in this time range." />
              ) : <div className="py-6 text-sm text-center text-muted-foreground">
                No Recent login found.
              </div>}
        </div>

        {!loading && recentLogins.length > itemsPerPage && (
          <div className="overview-pagination flex flex-wrap items-center justify-center gap-2 mt-2">
            {fromdetails && <span className="overview-pagination-summary">{loginStartIndex + 1}–{Math.min(loginStartIndex + itemsPerPage, recentLogins.length)} of {recentLogins.length}</span>}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentLoginPage((prev) => Math.max(1, prev - 1))}
              disabled={currentLoginPage === 1}
              aria-label="Previous login page"
              className="p-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            {getVisiblePages().map((page, idx) =>
              page === "..." ? (
                <span key={`dots-${idx}`} className="px-2 text-xs text-muted-foreground">
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  variant={currentLoginPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentLoginPage(Number(page))}
                  aria-current={currentLoginPage === page ? "page" : undefined}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              )
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentLoginPage((prev) => Math.min(totalLoginPages, prev + 1))}
              disabled={currentLoginPage === totalLoginPages}
              aria-label="Next login page"
              className="p-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
